import { useSelector, useDispatch } from 'react-redux';
import { useCallback } from 'react';
import {
  setPageState,
  setPageMultipleStates,
  setCurrentPage,
  setTotalRecords,
  setPageSize,
  setSearch,
  setSearchText,
  setSortValue,
  setSortType,
  setFilterData,
  setIsFilterUsing,
  setOnfilterPagination,
  setSelectedItems,
  setSelectedRowKeys,
  setColumnsvisible,
  setVisibleFilter,
  setVisibleSort,
  setDropdownVisible,
  setIsArchiveView,
  setArchiveMode,
  setArchiveTargetIds,
  setData,
  setLoadingTable,
  setCustomFilter,
  setCustomFilters,
  resetPageState,
  resetPageFilters,
  setGlobalLoading,
  setApiLoading,
  setCurrentRoute,
  addNotification,
  removeNotification,
  setGlobalModal,
  setConfirmModal
} from '../store/globalStateSlice';

// Custom hook for page-specific state management
export const usePageState = (pageName) => {
  const dispatch = useDispatch();
  const pageState = useSelector(state => state.globalState.pages[pageName] || state.globalState.pages.default);
  const globalState = useSelector(state => state.globalState.global);

  // Generic state setter
  const setState = useCallback((key, value) => {
    dispatch(setPageState({ pageName, key, value }));
  }, [dispatch, pageName]);

  // Set multiple states at once
  const setMultipleStates = useCallback((states) => {
    dispatch(setPageMultipleStates({ pageName, states }));
  }, [dispatch, pageName]);

  // Pagination actions
  const updateCurrentPage = useCallback((currentPage) => {
    dispatch(setCurrentPage({ pageName, currentPage }));
  }, [dispatch, pageName]);

  const updateTotalRecords = useCallback((totalRecords) => {
    dispatch(setTotalRecords({ pageName, totalRecords }));
  }, [dispatch, pageName]);

  const updatePageSize = useCallback((pageSize) => {
    dispatch(setPageSize({ pageName, pageSize }));
  }, [dispatch, pageName]);

  // Search and filter actions
  const updateSearch = useCallback((search) => {
    dispatch(setSearch({ pageName, search }));
  }, [dispatch, pageName]);

  const updateSearchText = useCallback((searchText) => {
    dispatch(setSearchText({ pageName, searchText }));
  }, [dispatch, pageName]);

  const updateSortValue = useCallback((sortValue) => {
    dispatch(setSortValue({ pageName, sortValue }));
  }, [dispatch, pageName]);

  const updateSortType = useCallback((sortType) => {
    dispatch(setSortType({ pageName, sortType }));
  }, [dispatch, pageName]);

  // Filter data actions
  const updateFilterData = useCallback((filterData) => {
    dispatch(setFilterData({ pageName, filterData }));
  }, [dispatch, pageName]);

  const updateIsFilterUsing = useCallback((isFilterUsing) => {
    dispatch(setIsFilterUsing({ pageName, isFilterUsing }));
  }, [dispatch, pageName]);

  const updateOnfilterPagination = useCallback((onfilterPagination) => {
    dispatch(setOnfilterPagination({ pageName, onfilterPagination }));
  }, [dispatch, pageName]);

  // Selected items actions
  const updateSelectedItems = useCallback((selectedItems) => {
    dispatch(setSelectedItems({ pageName, selectedItems }));
  }, [dispatch, pageName]);

  const updateSelectedRowKeys = useCallback((selectedRowKeys) => {
    dispatch(setSelectedRowKeys({ pageName, selectedRowKeys }));
  }, [dispatch, pageName]);

  // Column and UI actions
  const updateColumnsvisible = useCallback((columnsvisible) => {
    dispatch(setColumnsvisible({ pageName, columnsvisible }));
  }, [dispatch, pageName]);

  const updateVisibleFilter = useCallback((visibleFilter) => {
    dispatch(setVisibleFilter({ pageName, visibleFilter }));
  }, [dispatch, pageName]);

  const updateVisibleSort = useCallback((visibleSort) => {
    dispatch(setVisibleSort({ pageName, visibleSort }));
  }, [dispatch, pageName]);

  const updateDropdownVisible = useCallback((dropdownVisible) => {
    dispatch(setDropdownVisible({ pageName, dropdownVisible }));
  }, [dispatch, pageName]);

  // Archive actions
  const updateIsArchiveView = useCallback((isArchiveView) => {
    dispatch(setIsArchiveView({ pageName, isArchiveView }));
  }, [dispatch, pageName]);

  const updateArchiveMode = useCallback((archiveMode) => {
    dispatch(setArchiveMode({ pageName, archiveMode }));
  }, [dispatch, pageName]);

  const updateArchiveTargetIds = useCallback((archiveTargetIds) => {
    dispatch(setArchiveTargetIds({ pageName, archiveTargetIds }));
  }, [dispatch, pageName]);

  // Data actions
  const updateData = useCallback((data) => {
    dispatch(setData({ pageName, data }));
  }, [dispatch, pageName]);

  const updateLoadingTable = useCallback((loadingTable) => {
    dispatch(setLoadingTable({ pageName, loadingTable }));
  }, [dispatch, pageName]);

  // Custom filters actions
  const updateCustomFilter = useCallback((filterKey, filterValue) => {
    dispatch(setCustomFilter({ pageName, filterKey, filterValue }));
  }, [dispatch, pageName]);

  const updateCustomFilters = useCallback((customFilters) => {
    dispatch(setCustomFilters({ pageName, customFilters }));
  }, [dispatch, pageName]);

  // Reset actions
  const resetPage = useCallback(() => {
    dispatch(resetPageState({ pageName }));
  }, [dispatch, pageName]);

  const resetFilters = useCallback(() => {
    dispatch(resetPageFilters({ pageName }));
  }, [dispatch, pageName]);

  return {
    // State
    pageState,
    globalState,
    
    // Generic actions
    setState,
    setMultipleStates,
    
    // Pagination actions
    updateCurrentPage,
    updateTotalRecords,
    updatePageSize,
    
    // Search and filter actions
    updateSearch,
    updateSearchText,
    updateSortValue,
    updateSortType,
    
    // Filter data actions
    updateFilterData,
    updateIsFilterUsing,
    updateOnfilterPagination,
    
    // Selected items actions
    updateSelectedItems,
    updateSelectedRowKeys,
    
    // Column and UI actions
    updateColumnsvisible,
    updateVisibleFilter,
    updateVisibleSort,
    updateDropdownVisible,
    
    // Archive actions
    updateIsArchiveView,
    updateArchiveMode,
    updateArchiveTargetIds,
    
    // Data actions
    updateData,
    updateLoadingTable,
    
    // Custom filters actions
    updateCustomFilter,
    updateCustomFilters,
    
    // Reset actions
    resetPage,
    resetFilters
  };
};

// Custom hook for global state management
export const useGlobalState = () => {
  const dispatch = useDispatch();
  const globalState = useSelector(state => state.globalState.global);

  const updateGlobalLoading = useCallback((loading) => {
    dispatch(setGlobalLoading(loading));
  }, [dispatch]);

  const updateApiLoading = useCallback((loading) => {
    dispatch(setApiLoading(loading));
  }, [dispatch]);

  const updateCurrentRoute = useCallback((route) => {
    dispatch(setCurrentRoute(route));
  }, [dispatch]);

  const addGlobalNotification = useCallback((notification) => {
    dispatch(addNotification(notification));
  }, [dispatch]);

  const removeGlobalNotification = useCallback((index) => {
    dispatch(removeNotification(index));
  }, [dispatch]);

  const updateGlobalModal = useCallback((visible) => {
    dispatch(setGlobalModal(visible));
  }, [dispatch]);

  const updateConfirmModal = useCallback((visible) => {
    dispatch(setConfirmModal(visible));
  }, [dispatch]);

  return {
    globalState,
    updateGlobalLoading,
    updateApiLoading,
    updateCurrentRoute,
    addGlobalNotification,
    removeGlobalNotification,
    updateGlobalModal,
    updateConfirmModal
  };
};

// Custom hook for specific page state (shorthand)
export const useMobilisationState = () => usePageState('mobilisationManagement');
export const useCampaignState = () => usePageState('campaignManagement');
export const useUserManagementState = () => usePageState('userManagement');
export const useSubscriptionState = () => usePageState('subscriptionManagement');
export const useNewsFeedState = () => usePageState('newsFeed');
export const useArchiveState = () => usePageState('archive');
export const useSearchState = () => usePageState('search');
export const useProfileState = () => usePageState('profile');
export const useChatState = () => usePageState('chat');
export const useJobAdvertState = () => usePageState('jobAdvert');
